/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { createContext, useState } from "react";
import { defaultRichTextObjectPM } from "cms/components/components/defaults";
import { swapItems } from "utils/array/index";
import { cloneArray } from "hooks/hooks-helpers";
import { isArrayEmpty, isArrayNotEmpty, generateGuid } from "utils/empty-exists";
import { makeKey } from "utils/key-generator";
import AccordionSectionEdit from "cms/components/components/accordion/edit/section";
import Button from "@mui/material/Button";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { FormControlLabel, Switch } from "@mui/material";
import { Logger } from "cms/utils/log/logger";

const AccordionComponentContext = createContext(null);

/**
 * @param {object} props
 * @param {Link[]=} props.links
 * @param {(nextLinks: Link[]) => void} props.onChange
 * @param {import('react').ReactNode} props.children
 */
const AccordionComponentContextWrapper = (props) => {

    const {
        sections,
        onChangeSections,
        children,
        showSteps,
        onChangeShowSteps
    } = props;

    const [expanded, setExpanded] = useState(false);

    const addSection = () => {
        const guid = generateGuid();
        const newSection = {
            guid,
            tab_id: guid,
            title: "",
            isVisible: false,
            richtext: defaultRichTextObjectPM,
        };
        const newSections = cloneArray(sections);
        newSections.push(newSection);
        onChangeSections(newSections);
    };

    const deleteSection = (index) => {
        let newSections = cloneArray(sections);
        newSections.splice(index, 1);
        onChangeSections(newSections);
    };

    const moveSection = (index, direction) => {
        let newSections = cloneArray(sections);
        const targetIndex = direction === "DOWN" ? index + 1 : index - 1;
        const count = newSections.length;
        // don't allow moving beyond beginning or end
        if (targetIndex < 0 || targetIndex === count) {
            return;
        }
        newSections = swapItems(newSections, index, targetIndex);
        onChangeSections(newSections);
    };

    const updateSection = (index, section) => {
        let newSections = cloneArray(sections);
        newSections[index] = section;
        Logger.debug(newSections);
        onChangeSections(newSections);
    };

    const handleExpandChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <div
            className='simplicity-image-list'
        >
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                <Button
                    className='accordion-component-add-section-button'
                    size='medium'
                    startIcon={<AddCircleOutlineIcon />}
                    color={"primary"}
                    target='warning'
                    onClick={addSection}
                >
                    Add Accordion Section
                </Button>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
                <div
                    className='simplicity-accordion panel-group accordion'
                    data-group-type='accordion'
                    role='tablist'
                    aria-multiselectable='true'
                >
                    {isArrayEmpty(sections) && (
                        <div className='accordion-component-add-section'>
                            Click <em>&#34;Add Accordion Section&#34;</em> to add new section.
                        </div>
                    )}
                    {isArrayNotEmpty(sections) &&
                        sections.map((section, index) => (
                            <AccordionSectionEdit
                                key={makeKey("acc_comp", section.guid)}
                                section={section}
                                sectionCount={sections?.length}
                                index={index}
                                expanded={expanded}
                                handleExpandChange={handleExpandChange}
                                deleteSection={deleteSection}
                                moveSection={moveSection}
                                updateSection={updateSection}
                            />
                        ))
                    }
                </div>

                <FormControlLabel
                    label="Include step numbers beside each accordion section"
                    control={
                        <Switch
                            // we are defaulting to false for older accordion
                            checked={showSteps ?? false}
                            onChange={onChangeShowSteps}
                        />
                    }
                />
            </div>
            {/* add context for children   */}
            {children}
        </div>
    );
};
AccordionComponentContextWrapper.displayName = "AccordionComponentContextWrapper";
export {
    AccordionComponentContext,
    AccordionComponentContextWrapper
};

/**
 * @typedef {object} Link
 * @property {string} guid
 * @property {string} link
 * @property {number} position
 */
